import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useEffect, useState } from "react"
import { DropzoneState } from "react-dropzone"
import { DropDown } from "../../comps/dropdown"
import { CustomThemedResource } from "../../comps/image"
import {
	DropAreaForModel,
	MimeTypes,
} from "../../comps/simple-drop-area"
import { Arrow } from "../../pages/tools"
import { useShortCut } from "@kartyom/shared-components"
import {
	allIcon,
	imageIcon,
	landscapeIcon,
	portraitIcon,
	searchIcon,
	videoIcon,
} from "../dashboard/icons"
import { EditSegmentsAction } from "./response-message"

interface EditSegmentsContentProps {
	selected: EditSegmentsAction
	hasUploadedFiles: boolean
	stockMediaType: "image" | "video"
	selectedOrientation: "portrait" | "landscape" | null
	relatedSearch: string
	mediaTypeFilter: "image" | "video" | "all"
	websiteUrl: string
	dropzoneState: DropzoneState
	storedSearches: string[]
	setMediaTypeFilter: (v: "image" | "video" | "all") => void
	setStockLoading: () => void
	chooseStockMediaType: (v: "image" | "video") => void
	selectOrientation: (
		v: "portrait" | "landscape" | null,
	) => void
	searchStock: (query: string) => void
	addNewSearch: (v: string) => void
}

export function EditSegmentsContent(
	props: EditSegmentsContentProps,
) {
	const {
		selected,
		websiteUrl,
		selectedOrientation,
		stockMediaType,
		relatedSearch,
		mediaTypeFilter,
		dropzoneState,
		hasUploadedFiles,
		storedSearches,
		addNewSearch,
		setStockLoading,
		setMediaTypeFilter,
		chooseStockMediaType,
		searchStock,
		selectOrientation,
	} = props
	const [inputValue, setInputValue] = useState("")
	const [inputFocus, setInputFocus] = useState(false)
	const { t } = useTranslation()

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target
			if (target instanceof HTMLElement) {
				if (
					target.className.includes("stock-input") ||
					target.className.includes("searches-container")
				) {
					setInputFocus(true)
				} else {
					setInputFocus(false)
				}
			}
		}

		document.addEventListener(
			"mousedown",
			handleClickOutside,
		)

		return () => {
			document.removeEventListener(
				"mousedown",
				handleClickOutside,
			)
		}
	}, [])

	useShortCut({
		shortCut: "Enter",
		handler: () => {
			setStockLoading()
			setInputFocus(false)
			searchStock(inputValue)
			if (inputValue !== "") {
				addNewSearch(inputValue)
			}
		},
	})

	switch (selected) {
		case "lbl_upload":
			const formats = [
				"image/jpeg",
				"image/png",
				"video/mp4",
				"image/webp",
			] as MimeTypes[]

			return (
				<div className="flex h-[76px] items-center px-[16px] tablet:h-[80px]">
					<div
						className={clsx(
							hasUploadedFiles ? "flex" : "hidden",
							"w-full gap-[11px] tablet:justify-between",
						)}>
						<DropAreaForModel
							state={dropzoneState}
							openExplorer={dropzoneState.open}
							className="tablet:max-w-[214px]"
							formats={formats}
							noText
							button
						/>
						<DropDown
							className="relative z-10"
							closeOnClick
							closeInSelect
							trigger={
								<DropDownTrigger
									small={false}
									selected={mediaTypeFilter}
								/>
							}>
							<div
								className={clsx(
									"absolute z-20 flex w-[145px] flex-col gap-[2px] p-[10px]",
									"rounded-[8px] border border-color-separator bg-color-surface",
									"right-0 top-[50px]",
								)}>
								{[
									{
										name: "all" as const,
										icon: allIcon(),
									},
									{
										name: "image" as const,
										icon: imageIcon(),
									},
									{
										name: "video" as const,
										icon: videoIcon(),
									},
								].map((e) => (
									<button
										key={e.name}
										onClick={() =>
											setMediaTypeFilter(e.name)
										}
										className={clsx(
											mediaTypeFilter === e.name
												? "bg-blue-200"
												: "hover:bg-blue-100",
											"flex w-full items-center gap-[6px] rounded-[4px]",
											"px-[8px] py-[12px] [--icon-color:var(--color-blue-800)]",
										)}>
										{e.icon}
										<span className="text-[15px] font-500 leading-[18px] text-blue-800">
											{t(`lbl_${e.name}`)}
										</span>
									</button>
								))}
							</div>
						</DropDown>
					</div>
				</div>
			)

		case "txt_stock_media":
			return (
				<div
					className={clsx(
						"flex h-[160px] px-[16px] py-[12px] tablet:h-[114px] tablet:pt-[16px]",
						"desktop:h-[118px]",
					)}>
					<div
						className={clsx(
							"flex w-full flex-col gap-[16px] tablet:flex-row",
							"tablet:justify-between",
						)}>
						<div className="relative flex flex-col gap-[10px]">
							<div
								className={clsx(
									inputFocus
										? "border-blue-150"
										: "border-color-surface-cell",
									"relative flex w-full gap-[4px] bg-color-surface-cell pl-[14px]",
									"h-[44px] rounded-[6px] border pr-[34px] tablet:max-w-[437px]",
									"tablet:w-[440px] desktop:w-[520px] desktop:pl-[34px] desktop:pr-[14px]",
									"stock-input",
								)}>
								<input
									placeholder={t("lbl_search")}
									value={inputValue}
									onChange={(e) => {
										const inputValue = e.currentTarget.value
										if (inputValue.length <= 1000) {
											setInputValue(inputValue)
										}
									}}
									className={clsx(
										"text-[17px] font-400 placeholder:text-color-placeholder",
										"w-full bg-color-surface-cell outline-none",
										"stock-input",
									)}
								/>
								<button
									onClick={() => {
										searchStock(inputValue)
										setStockLoading()
										if (inputValue !== "") {
											addNewSearch(inputValue)
										}
									}}
									className={clsx(
										"absolute right-[14px] top-1/2 -translate-y-1/2 cursor-default",
										"[--icon-color:var(--color-placeholder)] desktop:left-[14px] desktop:right-auto",
										"stock-input",
									)}>
									{searchIcon()}
								</button>
							</div>

							<button
								onClick={() => {
									setInputValue(relatedSearch)
									searchStock(relatedSearch)
									setStockLoading()
								}}
								className={clsx(
									"flex items-center gap-[6px] rounded-[6px] bg-blue-150 p-[12px]",
									"min-w-max max-w-min",
								)}>
								<CustomThemedResource
									source="/general/search-icon"
									format="png"
									alt="search icon"
									className="h-[12px] w-[12px]"
								/>
								<span className="truncate text-[13px] font-400 leading-[12px] text-blue-800">
									{relatedSearch}
								</span>
							</button>

							{storedSearches &&
								storedSearches.length !== 0 && (
									<div
										className={clsx(
											inputFocus ? "flex" : "hidden",
											"absolute left-0 top-[54px] flex-col rounded-[8px]",
											"gap-[2px] border border-color-separator py-[12px]",
											"no-scrollbar max-h-[254px] w-full overflow-y-scroll",
											"searches-container z-40 bg-color-popup-cell",
										)}>
										<span
											className={clsx(
												"ml-[16px] text-[15px] font-500 leading-[20px] text-blue-500",
												"searches-container",
											)}>
											{t("txt_search_history")}
										</span>
										<div className="searches-container flex flex-col">
											{storedSearches.map((s, index) => (
												<button
													key={index}
													onClick={() => {
														setInputValue(s)
														searchStock(s)
														setInputFocus(false)
														setStockLoading()
													}}
													className={clsx(
														"h-[40px] w-full px-[16px] hover:bg-blue-100",
														"searches-container flex items-center",
													)}>
													<span
														className={clsx(
															"text-[15px] font-400 leading-[20px] text-blue-800",
															"searches-container",
														)}>
														{s}
													</span>
												</button>
											))}
										</div>
									</div>
								)}
						</div>

						<FilterSection
							stockMediaType={stockMediaType}
							selectedOrientation={selectedOrientation}
							selectOrientation={selectOrientation}
							chooseStockMediaType={chooseStockMediaType}
						/>
					</div>
				</div>
			)

		case "txt_website_images":
			return (
				<div className="flex h-[72px] items-center px-[16px] tablet:h-[100px]">
					<div
						className={clsx(
							"rounded-[10px] bg-gradient-to-r from-[#5DA2F6] to-[#B15DF6] p-[2px]",
							"tablet:mx-[12px] desktop:w-[661px] desktop:self-center",
						)}>
						<div
							className={clsx(
								"flex items-center gap-[6px] bg-color-cell",
								"h-[44px] w-full overflow-hidden rounded-[8px] pl-[14px]",
							)}>
							<CustomThemedResource
								source="/general/url-icon"
								format="svg"
								className="h-[16px] w-[16px]"
							/>
							<span
								className={clsx(
									"line-clamp-1 shrink-0 text-[17px] font-400 text-blue-800",
								)}>
								{websiteUrl}
							</span>
						</div>
					</div>
				</div>
			)
	}
}

function DropDownTrigger(props: {
	selected: string | null
	small: boolean
}) {
	const { t } = useTranslation()

	return (
		<div
			className={clsx(
				props.small ? "h-[34px]" : "h-[44px]",
				"flex items-center justify-center desktop:mt-[8px]",
				"w-full gap-[2px] rounded-[6px] bg-blue-200 desktop:h-[36px]",
				"pl-[10px] pr-[6px] desktop:bg-[unset] desktop:hover:bg-blue-200",
			)}>
			<span
				className={clsx(
					"text-[16px] font-500 text-blue-800",
				)}>
				{t(`lbl_${props.selected ?? "all"}`)}
			</span>
			<div
				className={clsx(
					"flex rotate-90 items-center justify-center",
					"transition-all [--icon-color:var(--color-blue-800)]",
				)}>
				<Arrow />
			</div>
		</div>
	)
}

interface FilterSectionProps {
	stockMediaType: "image" | "video"
	chooseStockMediaType: (v: "image" | "video") => void
	selectedOrientation: "portrait" | "landscape" | null
	selectOrientation: (
		v: "portrait" | "landscape" | null,
	) => void
}

function FilterSection(props: FilterSectionProps) {
	const {
		stockMediaType,
		selectOrientation,
		selectedOrientation,
		chooseStockMediaType,
	} = props
	const { t } = useTranslation()

	return (
		<div className="grid grid-cols-2 gap-[12px] tablet:flex">
			<DropDown
				className="relative z-10"
				triggerClassName="w-full"
				closeOnClick
				closeInSelect
				trigger={
					<DropDownTrigger
						selected={selectedOrientation}
						small
					/>
				}>
				<div
					className={clsx(
						"absolute z-20 flex flex-col gap-[2px] p-[10px]",
						"rounded-[8px] border border-color-separator bg-color-surface",
						"right-0 top-[50px] w-full tablet:w-[150px]",
					)}>
					{[
						{
							name: null,
							icon: allIcon(),
						},
						{
							name: "landscape" as const,
							icon: landscapeIcon(),
						},
						{
							name: "portrait" as const,
							icon: portraitIcon(),
						},
					].map((e) => (
						<button
							key={e.name}
							onClick={() => selectOrientation(e.name)}
							className={clsx(
								selectedOrientation === e.name
									? "bg-blue-200"
									: "hover:bg-blue-100",
								"flex w-full items-center gap-[6px] rounded-[4px]",
								"px-[8px] py-[12px] [--icon-color:var(--color-blue-800)]",
							)}>
							{e.icon}
							<span className="text-[15px] font-500 leading-[18px] text-blue-800">
								{e.name === null
									? t("lbl_all")
									: t(`lbl_${e.name}`)}
							</span>
						</button>
					))}
				</div>
			</DropDown>
			<DropDown
				className="relative z-10"
				triggerClassName="w-full"
				closeOnClick
				closeInSelect
				trigger={
					<DropDownTrigger
						selected={stockMediaType}
						small
					/>
				}>
				<div
					className={clsx(
						"absolute z-20 flex w-full flex-col gap-[2px] p-[10px]",
						"rounded-[8px] border border-color-separator bg-color-surface",
						"right-0 top-[50px] tablet:w-[150px]",
					)}>
					{[
						{
							name: "image" as const,
							icon: imageIcon(),
						},
						{
							name: "video" as const,
							icon: videoIcon(),
						},
					].map((e) => (
						<button
							key={e.name}
							onClick={() => chooseStockMediaType(e.name)}
							className={clsx(
								stockMediaType === e.name
									? "bg-blue-200"
									: "hover:bg-blue-100",
								"flex w-full items-center gap-[6px] rounded-[4px]",
								"px-[8px] py-[12px] [--icon-color:var(--color-blue-800)]",
							)}>
							{e.icon}
							<span className="text-[15px] font-500 leading-[18px] text-blue-800">
								{t(`lbl_${e.name}`)}
							</span>
						</button>
					))}
				</div>
			</DropDown>
		</div>
	)
}
